import { CanvasTexture, MeshStandardMaterial, SRGBColorSpace } from 'three';
import { SHOP_SIGNS } from '../../content/signs';
import { paintCompoundWall, paintConcreteOffice, paintGlass, paintShopFront, paintShophouse } from './facadePainters';
import type { FacadeKind } from './facadeLibrary';

/**
 * Every painted facade in one texture, so a whole street side can be a single mesh and a single draw call.
 * Each cell is drawn with a padded border so tiling with fract() doesn't bleed into neighbours.
 */
const SIZE = { w: 2048, h: 1024 } as const;
const PAD = 4;

export interface Cell {
  u: number;
  v: number;
  du: number;
  dv: number;
}

const GLASS_TINTS = ['#3b6e8f', '#2f6f64', '#27465a', '#5b7f95', '#6b5a48'];
const CONCRETE = ['#dedbd3', '#d3cec2', '#c7c2b6', '#dbd3c1'];
const PAINT = ['#d6c08a', '#c9936a', '#b56a47', '#8fadb8', '#e2dccd', '#c8a294', '#a3b894', '#d3cdb4'];
const WALLS = ['#e4ddcf', '#d8cbb0', '#ddd0b6', '#cfd4d3'];

type Source = () => CanvasTexture;

function sources(): Record<FacadeKind, Source[]> {
  return {
    glass: GLASS_TINTS.map((tint, i) => () => paintGlass(tint, i * 97 + 5)),
    concrete: CONCRETE.map((wall, i) => () => paintConcreteOffice(wall, i * 97 + 8)),
    shophouse: PAINT.map((wall, i) => () => paintShophouse(wall, i * 97 + 9)),
    shopfront: SHOP_SIGNS.map((sign, i) => () => paintShopFront(sign, i * 97 + 9)),
    wall: WALLS.map((wall, i) => () => paintCompoundWall(wall, i * 97 + 4)),
  };
}

class Atlas {
  readonly texture: CanvasTexture;
  private readonly cells = new Map<string, Cell>();
  private readonly ctx: CanvasRenderingContext2D;
  private x = 0;
  private y = 0;
  private rowHeight = 0;

  constructor() {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE.w;
    canvas.height = SIZE.h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    this.ctx = ctx;
    for (const [kind, list] of Object.entries(sources())) list.forEach((make, i) => this.place(`${kind}:${i}`, make().image as HTMLCanvasElement));
    this.texture = new CanvasTexture(canvas);
    this.texture.colorSpace = SRGBColorSpace;
    this.texture.anisotropy = 4;
  }

  cell(key: string): Cell {
    const cell = this.cells.get(key);
    if (!cell) throw new Error(`no atlas cell ${key}`);
    return cell;
  }

  /** A solid colour cell (roofs). Added on demand. */
  colour(color: string): Cell {
    const key = `colour:${color}`;
    if (!this.cells.has(key)) {
      const swatch = document.createElement('canvas');
      swatch.width = 16;
      swatch.height = 16;
      const sctx = swatch.getContext('2d');
      if (sctx) {
        sctx.fillStyle = color;
        sctx.fillRect(0, 0, 16, 16);
      }
      this.place(key, swatch);
      this.texture.needsUpdate = true;
    }
    return this.cell(key);
  }

  private place(key: string, image: HTMLCanvasElement): void {
    const w = image.width + PAD * 2;
    const h = image.height + PAD * 2;
    if (this.x + w > SIZE.w) {
      this.x = 0;
      this.y += this.rowHeight;
      this.rowHeight = 0;
    }
    if (this.y + h > SIZE.h) throw new Error('facade atlas is full');
    const { ctx } = this;
    // Stretch the image into the padding so edge texels repeat instead of bleeding.
    ctx.drawImage(image, this.x, this.y, w, h);
    ctx.drawImage(image, this.x + PAD, this.y + PAD);
    this.cells.set(key, {
      u: (this.x + PAD) / SIZE.w,
      v: 1 - (this.y + PAD + image.height) / SIZE.h,
      du: image.width / SIZE.w,
      dv: image.height / SIZE.h,
    });
    this.x += w;
    this.rowHeight = Math.max(this.rowHeight, h);
  }
}

/** 0 by day, 1 at night: lights up the signs and gives the buildings a faint glow. Shared by the atlas material. */
export const nightGlow = { value: 0 };

let atlas: Atlas | null = null;
let material: MeshStandardMaterial | null = null;

export function facadeAtlas(): Atlas {
  atlas ??= new Atlas();
  return atlas;
}

/** Lambert material that tiles each face inside its own atlas cell (per-vertex `cellRect`). */
export function atlasMaterial(): MeshStandardMaterial {
  if (material) return material;
  material = new MeshStandardMaterial({ map: facadeAtlas().texture, roughness: 0.85, metalness: 0.05 });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uNight = nightGlow;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec4 cellRect;\nvarying vec4 vCellRect;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvCellRect = cellRect;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec4 vCellRect;\nuniform float uNight;')
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        // At night: the very brightest paint (sign lettering, lit windows) glows, and everything is faintly lit from inside.
        float glowLuma = dot(sampledDiffuseColor.rgb, vec3(0.299, 0.587, 0.114));
        totalEmissiveRadiance += sampledDiffuseColor.rgb * uNight * (0.16 + 1.2 * smoothstep(0.84, 0.98, glowLuma));`,
      )
      .replace(
        '#include <map_fragment>',
        `vec2 atlasUv = vCellRect.xy + fract(vMapUv) * vCellRect.zw;
        vec2 gradScale = vCellRect.zw;
        vec4 sampledDiffuseColor = textureGrad(map, atlasUv, dFdx(vMapUv) * gradScale, dFdy(vMapUv) * gradScale);
        diffuseColor *= sampledDiffuseColor;`,
      );
  };
  return material;
}
